// @mui

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import * as Yup from 'yup';
import { DocumentFileDto } from 'woofwoof-api';

import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUploadDocuments } from 'src/hooks/useUploadDocuments';
import { getFileIcon } from 'src/sections/@dashboard/profile/components/ProfileFilesTable';
import FormProvider, { RHFTextField, RHFUploadBox } from '../hook-form';
import Iconify from '../iconify';
import { getFileUrl } from 'src/utils/getFileUrl';
import { translateKey } from 'src/locales/translate';


// ---------------------------------------------------------------------

export type FilesUploadModalFormValuesProps = {
  files: DocumentFileDto[] | null;
  description?: string;
};

const defaultValues: FilesUploadModalFormValuesProps = {
  files: null,
  description: '',
};

type FilesUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: DocumentFileDto[]) => void;
  isLoading: boolean;
  profileId?: string;
  visitId?: string;
};

export default function FilesUploadModal({
  isOpen,
  onClose,
  onUpload,
  isLoading,
  profileId,
  visitId,
}: FilesUploadModalProps) {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const { upload, downloadURLs, error, reset, remove } = useUploadDocuments({ profileId, visitId });
  const { enqueueSnackbar } = useSnackbar();
  const Schema: Yup.Schema<FilesUploadModalFormValuesProps> =
    Yup.object().shape({
      files: Yup.array()
        .of(
          Yup.object().shape({
            filename: Yup.string().required(),
            url: Yup.string().required(),
            originalFilename: Yup.string().optional(),
            uploadToken: Yup.string().optional(),
            description: Yup.string().optional(),
          }),
        )
        .required(),
      description: Yup.string().default(''),
    });

  const methods = useForm<FilesUploadModalFormValuesProps>({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const { setValue, handleSubmit, watch } = methods;

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const fileNamesToUpload = acceptedFiles.map((file) => file.name);
      const isFileAlreadyUploaded = fileNamesToUpload.some((item) =>
        downloadURLs.map((item) => item.name.split('_')[1]).includes(item),
      );

      // TODO: check if file name and extension of the file is the same, not just the name. We could display name of already uploaded file
      if (isFileAlreadyUploaded) {
        enqueueSnackbar(translateKey("snackbar.attachmentDuplicate"), {
          variant: 'error',
        });
        return;
      }
      setUploading(true);
      upload(acceptedFiles);
    },
    [upload, downloadURLs, enqueueSnackbar],
  );

  useEffect(() => {
    if (downloadURLs.length > 0) {
      const description = watch('description');
      setValue(
        'files',
        downloadURLs.map((file) => ({ 
          url: file.url, 
          filename: file.name,
          uploadToken: file.uploadToken,
          originalFilename: file.originalName,
          description: description || undefined
        })),
      );
      setUploading(false);
    }
  }, [downloadURLs, setValue, watch]);

  useEffect(() => {
    if (error) {
      setUploading(false);
    }
  }, [error]);

  const handleRemove = (fileName: string) => {
    remove(fileName);
  };

  const handleUpload = (values: FilesUploadModalFormValuesProps) => {
    if (values.files) {
      onUpload(values.files);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!isOpen && downloadURLs?.length > 0) {
      setTimeout(() => reset(), 200);
    }
  }, [downloadURLs, reset, isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleOnClose}
      PaperProps={{
        sx: { maxWidth: '600px', width: 0.8 },
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(handleUpload)}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Dodaj pliki
        </DialogTitle>
        <DialogContent sx={{ px: 3, overflow: 'hidden' }}>
          {downloadURLs?.length > 0 && (
            <Stack sx={{ gap: 2 }}>
              {downloadURLs?.map(({ name, url, progress, status, originalName }, index) => (
                <Stack
                  key={index}
                  sx={{
                    gap: 1.5,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: 2,
                      flex: 1,
                      cursor: progress === 100 && url ? 'pointer' : 'default',
                    }}
                    onClick={async () => {
                      if (progress === 100 && url) {
                        // Check if URL is a full URL or just a filename
                        const isFullUrl = url.startsWith('http');
                        
                        if (isFullUrl) {
                          // Public file - open directly
                          window.open(url, '_blank');
                        } else {
                          // Private file - get signed URL via the appropriate per-entity endpoint
                          const owner = visitId
                            ? { kind: 'visitFile' as const, visitId, filename: url }
                            : profileId
                            ? { kind: 'profileFile' as const, profileId, filename: url }
                            : null;
                          if (owner) {
                            try {
                              const signedUrl = await getFileUrl(owner);
                              window.open(signedUrl, '_blank');
                            } catch (error) {
                              console.error('Error fetching signed URL:', error);
                              enqueueSnackbar(translateKey("petAi.fileOpenError"), { variant: 'error' });
                            }
                          } else {
                            window.open(url, '_blank');
                          }
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        padding: 1,
                        borderRadius: 1,
                        backgroundColor: theme.palette.background.neutral,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {getFileIcon(originalName || name)}
                    </Box>
                    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 0.5 }}>
                      <Typography
                        sx={{
                          '&:hover': progress === 100 && url ? { textDecoration: 'underline' } : {},
                        }}
                      >
                        {originalName || name.split('_').slice(2).join('_') || name}
                      </Typography>
                      {progress < 100 && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {status || translateKey("search.fileProcessing")}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ width: 1, height: 5 }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => handleRemove(name)}
                    color="error"
                    disabled={uploading}
                  >
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
          <RHFUploadBox
            sx={{
              flex: 1,
              height: 'inherit',
              borderRadius: 1.5,
              width: 1,
              minHeight: 150,
              mt: 2,
            }}
            name="background"
            maxSize={3145728}
            onDrop={(files) => handleDrop(files)}
            placeholder={
              <Stack sx={{ gap: 0.5, p: 3, flex: 1 }} alignItems="center">
                {!uploading ? (
                  <>
                    <Iconify icon="eva:cloud-upload-fill" width={40} />
                    <Typography variant="body2">{translateKey("quickBooking.uploadFiles")}</Typography>
                  </>
                ) : null}
              </Stack>
            }
            disabled={isLoading || uploading}
          />
          <Stack sx={{ gap: 2, mt: 2 }}>
            <RHFTextField
              name="description"
              label={translateKey("hardcoded.opis_dokumentow")}
              placeholder={translateKey("hardcoded.dodaj_opis_ktory_pomoze_w_wyszukiwaniu_i_analizie")}
              multiline
              rows={3}
              helperText={translateKey("hardcoded.ten_sam_opis_zostanie_dodany_do_wszystkich_przeslanych")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleOnClose}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            size="medium"
            loading={isLoading}
            disabled={isLoading || uploading || downloadURLs?.length === 0 || downloadURLs.some((f) => f.progress < 100)}
          >
            <span>{translateKey("common.save")}</span>
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
